// src/components/Platform/Orders/OrdersList/OrdersList.jsx

import { useContext, useEffect, useMemo, useState } from "react";
import PageHeading from "../../PageHeading/PageHeading";
import OrderTimer from "../OrderPageV2/OrderTimer/OrderTimer";
import "./OrdersList.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getCurrentPlatfromLocation,
  parseApiError,
  parsePrice,
  parseRequestNumber,
  parseStatus,
  createAction,
} from "../../../../assets/utils/utils";
import {
  ORDERS_ITEM_DEFAULT_LINK,
  ORDERS_ITEM_MAIN_INFO_LINK,
  ORDERS_LIMIT,
  ORDERS_MAIN_LINK,
  PLATFORM_MAIN_LINK,
} from "../../../../assets/utils/constants";
import { UserContext } from "../../../../assets/contexts/userContext";
import mainApi from "../../../../assets/api/MainApi";
import MiniPreloader from "../../../MiniPreloader/MiniPreloader";
import useAutoDismissError from "../../../../assets/hooks/useAutoDismissError";
import ErrorMessage from "../../../ErrorMessage/ErrorMessage";
import { PlatformLinksContext } from "../../../../assets/contexts/platformLinksContext";
import OrdersScanPopup from "./OrdersScanPopup";

function OrdersList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { links } = useContext(PlatformLinksContext);

  const current_location = getCurrentPlatfromLocation({
    location,
    links,
  });

  const [orders, setOrders] = useState(undefined);
  const [isPreloaderVisible, setIsPreloaderVisible] = useState(true);
  const [isFeedPreloader, setIsFeedPreloader] = useState(false);
  const [isMoreBtnVisible, setIsMoreBtnVisible] = useState(false);
  const [error, showError] = useAutoDismissError();
  const [isScanPopupOpen, setIsScanPopupOpen] = useState(false);

  useEffect(() => {
    if (!user) return;

    setIsPreloaderVisible(true);
    getOrders({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function getOrders({ last_id }) {
    const shop_id = user?.default_shop?._id;
    mainApi
      .getOrders({ shop_id, limit: ORDERS_LIMIT, last_id })
      .then((res) => {
        const data = res.data;
        setOrders(last_id ? (prevArr) => prevArr.concat(data) : data);
        setIsMoreBtnVisible(res.is_more);
      })
      .catch((err) => {
        if (err.statusCode === 403) {
          setOrders([]);
          setIsMoreBtnVisible(false);
        }
        showError(parseApiError(err));
      })
      .finally(() => {
        setIsPreloaderVisible(false);
        setIsFeedPreloader(false);
      });
  }

  function getMore() {
    const last_id = orders[orders.length - 1]._id;
    setIsFeedPreloader(true);
    getOrders({ last_id });
  }

  const hasOrders = Array.isArray(orders) && orders.length > 0;

  const actions = useMemo(
    () => [
      createAction("Отсканировать", () => setIsScanPopupOpen(true), {
        isMainAction: true,
      }),
    ],
    []
  );

  function handleOrderNavigate(orderId) {
    if (!orderId) return;
    navigate(
      `${PLATFORM_MAIN_LINK}/${ORDERS_MAIN_LINK}/${ORDERS_ITEM_DEFAULT_LINK}/${orderId}`
    );
    setIsScanPopupOpen(false);
  }

  return (
    <div className="orders-list">
      <PageHeading
        className={"orders-list__heading"}
        title={current_location.location.name}
        actions={actions}
      />

      <ErrorMessage error={error} />

      <div className="orders-list__content">
        {!isPreloaderVisible ? (
          <>
            {hasOrders ? (
              <div className="orders-list__grid">
                {orders.map((item) => {
                  const requestNumber = parseRequestNumber(item.ai_id);
                  const statusLabel = parseStatus(item.status, item.payment_type, item.delivery_type);
                  const fullName = [item.first_name, item.last_name]
                    .filter(Boolean)
                    .join(" ");
                  const detailsPath = `${PLATFORM_MAIN_LINK}/${ORDERS_MAIN_LINK}/${ORDERS_ITEM_DEFAULT_LINK}/${item._id}`;
                  const showHoldTimer =
                    item?.status === "paid" &&
                    item?.payment_type === "yookassa" &&
                    item?.utc_date;

                  return (
                    <Link
                      to={detailsPath}
                      className={`order-card  order-card_status_${item.status}`}
                      key={item._id}
                    >
                      <header className="order-card__header">
                        <div className="order-card__title-block">
                          <span className="order-card__label">
                            Номер заказа
                          </span>
                          <p className="order-card__number">
                            {requestNumber}
                          </p>
                        </div>
                        <span
                          className={`order-card__status`}
                        >
                          {statusLabel}
                        </span>
                      </header>

                      <div className="order-card__body">
                        <div className="order-card__row">
                          <span className="order-card__label">Клиент</span>
                          <span className="order-card__value">
                            {fullName || "Не указано"}
                          </span>
                        </div>

                        <div className="order-card__row order-card__row--two">
                          <div className="order-card__col">
                            <span className="order-card__label">Телефон</span>
                            <span className="order-card__value order-card__value_link">
                              {item.phone || "—"}
                            </span>
                          </div>
                          <div className="order-card__col">
                            <span className="order-card__label">Почта</span>
                            <span className="order-card__value order-card__value_link">
                              {item.email || "—"}
                            </span>
                          </div>
                        </div>

                        <div className="order-card__row order-card__row--two">
                          <div className="order-card__col">
                            <span className="order-card__label">Товаров</span>
                            <span className="order-card__value">
                              {item.total_items ?? "—"}
                            </span>
                          </div>
                          <div className="order-card__col order-card__col--right">
                            <span className="order-card__label">Сумма</span>
                            <span className="order-card__price">
                              {parsePrice(item.total_price, 'RUB')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <footer
                        className={`order-card__footer ${showHoldTimer ? "order-card__footer_with-timer" : ""
                          }`}
                      >
                        {showHoldTimer ? (
                          <>
                            <OrderTimer order={item} compact />
                            <Link
                              to={detailsPath}
                              className="order-card__details-btn order-card__details-btn_compact"
                            >
                              Подробнее
                            </Link>
                          </>
                        ) : (
                          <Link
                            to={detailsPath}
                            className="order-card__details-btn"
                          >
                            Подробнее
                          </Link>
                        )}
                      </footer>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="orders-list__empty">
                <div className="orders-list__empty-icon">📭</div>
                <p className="orders-list__empty-title">
                  Вы ещё не получали заказов
                </p>
                <p className="orders-list__empty-text">
                  Как только появится первый заказ, он отобразится здесь в
                  списке.
                </p>
              </div>
            )}

            {isMoreBtnVisible && hasOrders && (
              <button
                className={`orders-list__more-btn ${isFeedPreloader ? "orders-list__more-btn_disabled" : ""
                  }`}
                onClick={getMore}
                type="button"
              >
                <p
                  className={`orders-list__more-btn-text ${!isFeedPreloader
                    ? "orders-list__more-btn-text_visible"
                    : ""
                    }`}
                >
                  Загрузить ещё
                </p>
                <span
                  className={`orders-list__btn-preloader ${isFeedPreloader
                    ? "orders-list__btn-preloader_visible"
                    : ""
                    }`}
                >
                  <MiniPreloader />
                </span>
              </button>
            )}
          </>
        ) : (
          <div className="orders-list__preloader">
            <MiniPreloader />
          </div>
        )}
      </div>

      <OrdersScanPopup
        isOpen={isScanPopupOpen}
        onClose={() => setIsScanPopupOpen(false)}
        onDetect={handleOrderNavigate}
      />
    </div>
  );
}

export default OrdersList;
